<template>
  <div v-if="modelValue">
    <!-- Street / House Number row -->
    <div class="grid grid-cols-1 gap-x-10 md:grid-cols-2">
      <div class="flex flex-col">
        <label class="core-form-label" for="street">{{ wt('details', 'street') }}</label>
        <input
          type="text"
          id="street"
          v-model.lazy="modelValue.street"
          class="core-form-input core-form-input--full !mb-2"
          :class="{
            'core-form-error': validator.getErrors('street'),
          }" />
        <ErrorMessage name="street" class="error-message" />
      </div>
      <div class="flex flex-col">
        <label class="core-form-label" for="houseNumber">{{ wt('details', 'houseNumber') }}</label>
        <input
          type="text"
          id="houseNumber"
          v-model.lazy="modelValue.houseNumber"
          class="core-form-input core-form-input--full !mb-2"
          :class="{
            'core-form-error': validator.getErrors('houseNumber'),
          }" />
        <ErrorMessage name="houseNumber" class="error-message" />
      </div>
    </div>

    <!-- Zip / City row -->
    <div class="grid grid-cols-1 gap-x-10 md:grid-cols-2">
      <div class="flex flex-col">
        <label class="core-form-label" for="zip">{{ wt('details', 'zip') }}</label>
        <input
          type="text"
          id="zip"
          v-model.lazy="modelValue.zip"
          class="core-form-input core-form-input--full !mb-2"
          :class="{ 'core-form-error': validator.getErrors('zip') }" />
        <ErrorMessage name="zip" class="break-words error-message" />
      </div>
      <div class="flex flex-col">
        <label class="core-form-label" for="city">{{ wt('details', 'city') }}</label>
        <input
          type="text"
          id="city"
          v-model.lazy="modelValue.city"
          class="core-form-input core-form-input--full !mb-2"
          :class="{ 'core-form-error': validator.getErrors('city') }" />
        <ErrorMessage name="city" class="error-message" />
      </div>
    </div>

    <!-- Status dropdown -->
    <div class="flex flex-col items-start">
      <label class="core-form-label" for="status">{{ wt('details', 'status', 'label') }}</label>
      <div class="core-form-select !mb-2">
        <select
          id="status"
          v-model.lazy="modelValue.status"
          :class="{ 'core-form-error': validator.getErrors('status') }">
          <option v-for="(value, status) in statusSelect" :key="status" :value="value">
            {{ wt('details', 'status', 'option', value) }}
          </option>
        </select>
      </div>
      <ErrorMessage name="status" class="error-message" />
    </div>

    <!-- Description textarea -->
    <div class="flex flex-col">
      <label class="core-form-label" for="description">{{ wt('details', 'description') }}</label>
      <textarea
        class="core-form-textarea !mt-0"
        id="description"
        placeholder="..."
        v-model.lazy="modelValue.description"
        rows="10"
        cols="50"
        :class="{
          'core-form-error': validator.getErrors('description'),
        }" />
      <ErrorMessage name="description" class="error-message" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useValidator } from '@/validation/form-validator'
import { type PropType } from 'vue'
import { ProjectStatus } from '@/schemas/general/enums'
import ErrorMessage from '@/components/shared/ErrorMessage.vue'
import { useWidgetText } from '@/helpers/useWidgetText'

const { wt } = useWidgetText('widget-texts-project-general')

interface ProjectDetailsFormType {
  street: string
  houseNumber: string
  zip: string
  city: string
  status: string
  description: string
}

const modelValue = defineModel({ type: Object as PropType<ProjectDetailsFormType> })
const validator = useValidator()
const statusSelect = ProjectStatus
</script>
